/**
 * Simplified JSX implementation for JelenJS
 */
import { h } from './dom';
import { isSignal, getSignalValue } from '../reactive/utils';
import { createEffect } from '../reactive';
import { DEBUG_MODE, debugLog } from '../reactive/debug';
import { setCurrentComponent, markAsMounted } from './component';

// Type definitions
export type JSXElement = string | number | boolean | null | undefined | Node | Element | any[];

export interface JSXProps {
    children?: JSXElement | JSXElement[];
    [key: string]: any;
}

export type Component<P = any> = (props: P) => Element | HTMLElement | null;

/**
 * Handle reactive expressions in JSX props and children
 */
function processReactiveValue(value: any): any {
    // If it's a function (which might be a signal accessor like count()),
    // try to call it and get the current value
    if (typeof value === 'function') {
        try {
            const result = value();
            return result;
        } catch (e) {
            // If it fails, it's not a signal accessor function
            return value;
        }
    }
    
    // For regular signals, use getSignalValue
    if (isSignal(value)) {
        const result = getSignalValue(value);
        return result;
    }
    
    return value;
}

/**
 * Create a reactive wrapper for JSX expressions that might contain conditions
 * This is used to ensure conditional expressions remain reactive
 */
function createReactiveExpression(expr: any): () => any {
    // Return a function that will be evaluated in an effect
    return () => {
        // If the expression is already a function, call it
        if (typeof expr === 'function') {
            try {
                const result = expr();
                return result;
            } catch (e) {
                return expr;
            }
        }
        
        // If it's a signal, get its value
        if (isSignal(expr)) {
            const result = getSignalValue(expr);
            return result;
        }
        
        // Otherwise return the expression as is
        return expr;
    };
}

/**
 * Check if an expression contains signal calls that make it reactive
 */
function containsSignalCalls(expr: any): boolean {
    if (typeof expr === 'string') {
        // Check for signal call patterns in string expressions
        return /\w+\(\)/.test(expr);
    }
    
    if (typeof expr === 'function') {
        // It's already a function, likely reactive
        return true;
    }
    
    return false;
}

/**
 * Simple check for conditional expressions in runtime
 * This is a lightweight runtime check, not a full AST analysis
 */
function looksLikeConditionalExpression(expr: string): boolean {
    // Simple pattern matching for common conditional patterns
    return /\?\s*.*\s*:/.test(expr) || /&&/.test(expr) || /\|\|/.test(expr);
}

/**
 * Auto-wrap conditional expressions for reactivity
 */
function autoWrapConditional(expr: any): () => any {
    // If it's already a function, return it as-is
    if (typeof expr === 'function') {
        return expr;
    }
    
    // If it's a string that looks like a conditional expression with signals
    if (typeof expr === 'string' && looksLikeConditionalExpression(expr)) {
        // Create a function that evaluates the expression in the current scope
        // This is experimental and requires the signals to be in global scope
        try {
            return new Function('count', `return ${expr}`) as () => any;
        } catch (e) {
            if (DEBUG_MODE) debugLog(`Failed to create function from expression: ${expr}`, e);
            return () => expr;
        }
    }
    
    // Return a simple function that returns the value
    return () => expr;
}

/**
 * Process JSX children and make them reactive when needed
 */
function processJSXChildren(children: any): any[] {
    if (children == null) return [];
    
    // Handle arrays of children
    if (Array.isArray(children)) {
        return children.flatMap(processJSXChildren);
    }
    
    // Handle conditional expressions - the key part for automatic conditional rendering
    if (typeof children === 'object' && children !== null && 
        (('operator' in children && children.operator === '&&') || 
         ('test' in children && 'consequent' in children && 'alternate' in children))) {
        
        // Create a reactive function that will evaluate the condition
        const reactiveCondition = () => {
            if ('operator' in children && children.operator === '&&') {
                // For logical AND expressions (condition && <Element/>)
                const testValue = processReactiveValue(children.left);
                return testValue ? children.right : null;
            } else if ('test' in children) {
                // For ternary expressions (condition ? <TrueElement/> : <FalseElement/>)
                const testValue = processReactiveValue(children.test);
                return testValue ? children.consequent : children.alternate;
            }
            return null;
        };
        
        return [reactiveCondition];
    }
    
    // EXPERIMENTAL: Check if this might be a conditional expression string
    if (typeof children === 'string' && looksLikeConditionalExpression(children)) {
        if (DEBUG_MODE) debugLog(`Processing string conditional expression: ${children}`);
        // Auto-wrap the conditional expression
        return [autoWrapConditional(children)];
    }
    
    // Check if this is a simple boolean result of a condition
    if (typeof children === 'boolean') {
        return [children];
    }
    
    // Check if this might be a signal or a function that should be reactive
    if (typeof children === 'function' || isSignal(children)) {
        return [children]; // Pass the original signal directly, don't wrap it
    }
    
    return [children];
}

/**
 * Factory function for JSX elements
 */
export function jsx(tag: string | Component, props: JSXProps = {}) {
    // Extract children from props
    const { children, ...restProps } = props;
    
    // Handle function components
    if (typeof tag === 'function') {
        // Set component context for lifecycle hooks
        const componentInstance = tag;
        setCurrentComponent(componentInstance);
        
        try {
            const result = tag({ ...restProps, children });
            
            // Schedule mount callbacks to run after the component is rendered
            setTimeout(() => {
                markAsMounted(componentInstance);
            }, 0);
            
            return result;
        } finally {
            // Clear component context
            setCurrentComponent(null);
        }
    }

    // Process children
    const childrenArray = children === undefined ? [] : processJSXChildren(children);

    // Create element with hyperscript function
    return h(tag, restProps, ...childrenArray);
}

// Alias for JSX with spread children
export const jsxs = jsx;

// JSX Fragment implementation
export const Fragment = (props: { children?: JSXElement | JSXElement[] }) => {
    if (!props.children) {
        return document.createDocumentFragment();
    }

    const fragment = document.createDocumentFragment();
    
    // Process children
    const childrenArray = processJSXChildren(props.children);

    for (const child of childrenArray) {
        if (child instanceof Node || child instanceof Element) {
            fragment.appendChild(child);
        } else if (typeof child === 'function') {
            // Handle reactive functions (from conditional expressions)
            const container = document.createElement('span');
            container.style.display = 'contents';
            fragment.appendChild(container);
            
            // Set up effect to reactively update the content
            createEffect(() => {
                try {
                    const result = child();
                    
                    // Clear container
                    container.innerHTML = '';
                    
                    // Update with new content
                    if (result instanceof Node) {
                        container.appendChild(result);
                    } else if (result != null) {
                        container.textContent = String(result);
                    }
                } catch (e) {
                    // Handle errors silently
                }
            });
        } else if (child != null) {
            fragment.appendChild(document.createTextNode(String(child)));
        }
    }

    return fragment;
};

/**
 * Create an element using a more familiar syntax similar to React.createElement
 */
export function createElement(
    tag: string | Component,
    props: JSXProps | null = null,
    ...children: any[]
): Element | HTMLElement | DocumentFragment {
    if (typeof tag === 'function') {
        // Set component context for lifecycle hooks
        const componentInstance = tag;
        setCurrentComponent(componentInstance);
        
        try {
            const result = tag({ ...props, children }) || document.createDocumentFragment();
            
            // Schedule mount callbacks to run after the component is rendered
            setTimeout(() => {
                markAsMounted(componentInstance);
            }, 0);
            
            return result;
        } finally {
            // Clear component context
            setCurrentComponent(null);
        }
    }
    
    return h(tag, props || {}, ...children);
}